/**
 * Draft Assist Service — AI-powered proposal drafting suggestions
 *
 * Story BL-045.4: Proposal Drafting Assistant (AC6)
 *
 * Fetches proposal draft suggestions from oracle-bridge draft-assist endpoint.
 * Uses getApiBaseUrl() pattern from apiHelpers.ts.
 * Never throws — returns empty suggestions on error for graceful degradation.
 */

import { authenticatedFetch } from '@hello-world-dao/auth';
import { getApiBaseUrl } from './apiHelpers';

export interface DraftSuggestion {
  section: 'motivation' | 'budget' | 'timeline' | 'success_criteria' | 'risks';
  suggestion: string;
  severity: 'missing' | 'weak' | 'tip';
}

export interface DraftAssistResponse {
  suggestions: DraftSuggestion[];
  model: string;
}

/**
 * Get AI-generated suggestions for improving a governance proposal draft.
 *
 * @param title - Proposal title
 * @param body - Proposal body/content text
 * @returns DraftAssistResponse — never throws, returns empty suggestions on error
 */
export async function getDraftSuggestions(
  title: string,
  body: string,
): Promise<DraftAssistResponse> {
  try {
    const response = await authenticatedFetch(`${getApiBaseUrl()}/api/governance/proposals/draft-assist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title, body }),
    });
    if (!response.ok) return { suggestions: [], model: 'unavailable' };
    return response.json() as Promise<DraftAssistResponse>;
  } catch {
    return { suggestions: [], model: 'unavailable' };
  }
}
